import {
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    tableCellClasses,
    Paper,
    TablePagination,
    Box,
    Alert,
    AlertTitle,
    Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import colorConfigs from "../../configs/colorConfigs";

// Styled table row component - updated for better hover effects
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
        backgroundColor: "#FFFFFF",
        borderBottom: "none",
    },
    "&:nth-of-type(odd)": {
        backgroundColor: "#FAFAFA", // Slightly different shade for odd rows
        borderBottom: "none",
    },
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        backgroundColor: `${colorConfigs.primary}30`,
        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
        transform: "translateY(-2px)",
    },
}));

// Styled table cell component - updated for better typography
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: colorConfigs.primary,
        color: colorConfigs.secondary,
        fontSize: 16,
        fontWeight: 600,
        padding: "16px",
        borderBottom: `2px solid ${colorConfigs.secondary}20`,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 15,
        padding: "16px", 
        color: colorConfigs.secondary,
    },
}));

const PatientsTable = ({ data }) => {
    const [showAlert, setShowAlert] = useState(false);

    const navigate = useNavigate();

    const handleFetchPatientId = async (patientId) => {
        try {
            // const hasTrials = await checkIfPatientHasTrials(patientId);
            // if (hasTrials) {
            //     navigate(`/dashboard/${patientId}`);
            // } else {
            //     setShowAlert(true);
            // }
            navigate(`/dashboard/${patientId}`);
        } catch (error) {
            console.error("Error fetching trial data: ", error);
        }
    }

    // Handle pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    return (
        <Box>
            {showAlert && (
                <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} autoHideDuration={3000} open={showAlert} onClose={() => setShowAlert(false)}>
                    <Alert severity="warning" onClose={() => setShowAlert(false)} sx={{ width: '500px' }}>
                        <AlertTitle>No Trials</AlertTitle>
                        This patient has no trials
                    </Alert>
                </Snackbar>
            )}
            <TableContainer 
                component={Paper} 
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: `1px solid ${colorConfigs.primary}40`,
                }}
            >
                <Table>
                    {/* Table Head */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Patient Name</StyledTableCell>
                            <StyledTableCell>Email</StyledTableCell>
                            <StyledTableCell>Condition</StyledTableCell>
                            <StyledTableCell>Last Date Used</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    {/* Table Body */}
                    <TableBody>
                        {data
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                                <StyledTableRow 
                                    hover 
                                    key={index} 
                                    sx={{ 
                                        cursor: 'pointer',
                                    }} 
                                    onClick={() => handleFetchPatientId(row.id)}
                                >
                                    <StyledTableCell>{row.name}</StyledTableCell>
                                    <StyledTableCell>{row.email}</StyledTableCell>
                                    <StyledTableCell>{row.medicalCondition}</StyledTableCell>
                                    <StyledTableCell>{row.lastDateUsed}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                    </TableBody>
                </Table>
                {/* Pagination component */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        borderTop: `1px solid ${colorConfigs.primary}40`,
                        color: colorConfigs.secondary,
                        '.MuiTablePagination-selectIcon': { 
                            color: colorConfigs.secondary 
                        },
                        '.MuiTablePagination-select': {
                            fontWeight: 500,
                        },
                        '.MuiTablePagination-selectLabel': {
                            fontWeight: 500,
                        },
                        '.MuiTablePagination-displayedRows': {
                            fontWeight: 500,
                        },
                        '.MuiIconButton-root': {
                            color: colorConfigs.secondary,
                        },
                        '.Mui-disabled': {
                            opacity: 0.4,
                        },
                    }}
                />
            </TableContainer>
        </Box>
    );
}

export default PatientsTable;
